import React, { useMemo, useState } from 'react';
import { ErrorState } from '../../shared/StateMessage';
import { SkeletonText } from '../../shared/Skeleton';
import { fmtCount } from '../../shared/format';
import AnswerCard from '../answers/AnswerCard';
import PlaceDetail from '../drill/PlaceDetail';
import RequestForm from '../drill/RequestForm';
import CallPanel from '../drill/CallPanel';
import SendToGuest from '../drill/SendToGuest';
import { RequestRow } from '../RequestsWorklistPanel';
import AroundMap from '../AroundMapPanel';
import { BTN, chipClass, PILL, ROW_BUTTON } from '../liStyles';
import useClientPagination from '../../../hooks/useClientPagination';
import Pagination from '../../shared/Pagination';

// Item 3 — frontend pagination for the answer list. The full dataset is
// already in memory (result.answers or the right-now blocks), so this is a
// pure client-side slice. 4 per page fills the right column's height now that
// the left column is taller (TYPE OF PLACE label + the MEALS / THINGS TO DO /
// ERRANDS & SERVICES need-tile group labels + PARTY / ACCESS / WHEN / ASK).
// Every pagination concern reads this one constant — useClientPagination
// (page count + pageItems slice), the (page - 1) * ANSWER_PAGE_SIZE keyboard-
// nav index, and <Pagination perPage={ANSWER_PAGE_SIZE}> (the "Showing X–Y of
// Z places" footer + numbered page controls) — so a change here propagates
// consistently with no other edits. Bump to 5 if visual inspection shows the
// right column still has room for another card.
const ANSWER_PAGE_SIZE = 4;

// Item 3 — drill-list "Load more" page size. The drill RPC (fn_li_drill) returns
// 50 rows per call with a separate `total` (the full match count), so the list
// reveals VENUE_PAGE_SIZE rows at a time from the loaded batch and fetches the
// next 50 when the visible slice outruns what is loaded.
const VENUE_PAGE_SIZE = 20;

// Item 3 (always-show pagination) — the answer panel always shows its footer,
// even for a single page, so the panel bottom edge stays stable. Passed via the
// shared <Pagination> component's `alwaysShow` prop (default false — all other
// callers across the dashboard are unchanged), so this page uses the same
// pagination control as every other dashboard page; no local footer clone.

// Page results body (Item 4 / Item 5) — the unified full-width results container. State
// gate: a query is active when the page holds an answer result; before that, show the
// "Right now" default suggestions. Both branches render the same AnswerCard, so the
// badges and action buttons are identical across states. A List / Map toggle (Item 5)
// swaps the cards for AroundMap, scoped to whichever dataset is on screen — the
// default suggestions when no query is active, the filtered answers when one is.
const PageResultsBody = ({ query, rightNow, mapPanel, whenLabel, onOpen, onOpenDrill, onBook, onOutcome, onAnswerListKey, answerActiveIdx }) => {
  const [view, setView] = useState('list');
  // Item 3 — the full answer set is already in memory (the query result or the
  // right-now blocks). Memoized so its reference is stable across renders that
  // don't change the data; useClientPagination resets to page 1 whenever this
  // reference changes (a new result arrives → need/filter change → page 1).
  const answers = useMemo(() => {
    const r = query?.result;
    return r
      ? (Array.isArray(r.answers) ? r.answers : [])
      : (Array.isArray(rightNow?.facts?.blocks) ? rightNow.facts.blocks.flatMap((b) => (Array.isArray(b.answers) ? b.answers : [])) : []);
  }, [query?.result, rightNow]);
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(answers, { pageSize: ANSWER_PAGE_SIZE });
  if (query?.loading) return <SkeletonText lines={3} lineClass="h-24" />;
  if (query?.error) return <ErrorState message={query.error} variant="strip" />;
  const result = query?.result;

  // The current dataset's answers — the list renders these as cards; the map is scoped
  // to them so List and Map are two renderings of the same set, not two page sections.
  const ids = new Set(answers.map((a) => a.entity_id));

  // Scope the map to the current dataset: keep only pins whose place is in the answers,
  // and recount the drive-time bands against that filtered set so the chips stay true.
  const allPins = Array.isArray(mapPanel?.facts?.pins) ? mapPanel.facts.pins : [];
  const bands = Array.isArray(mapPanel?.facts?.bands) ? mapPanel.facts.bands : [];
  const scopedPins = allPins.filter((p) => ids.has(p.entity_id));
  const bandCounts = bands.map(() => 0);
  scopedPins.forEach((p) => {
    if (p.drive_minutes == null) return;
    const i = bands.findIndex((b) => p.drive_minutes <= b.max_minutes);
    const idx = i === -1 ? bands.length - 1 : i;
    if (idx >= 0 && idx < bandCounts.length) bandCounts[idx] += 1;
  });
  const scopedBands = bands.map((b, i) => ({ ...b, places: bandCounts[i] }));
  const scopedMapPanel = mapPanel ? { ...mapPanel, facts: { ...mapPanel.facts, pins: scopedPins, bands: scopedBands } } : null;

  const toggle = (
    <div className="flex items-center gap-1" role="group" aria-label="Results view">
      <button type="button" aria-pressed={view === 'list'} className={chipClass(view === 'list')} onClick={() => setView('list')}>List</button>
      <button type="button" aria-pressed={view === 'map'} className={chipClass(view === 'map')} onClick={() => setView('map')}>Map</button>
    </div>
  );

  if (result) {
    return (
      <div className="flex flex-col gap-3" data-testid="li-answers">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            {result.need?.label ? (
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {result.need.label}
                <span className="font-normal text-gray-500 dark:text-gray-400">
                  {' · '}{whenLabel}{typeof result.total === 'number' ? ` · ${fmtCount(result.total)} ${result.total === 1 ? 'place' : 'places'}` : ''}
                </span>
              </p>
            ) : (
              <>
                {result.understood_label && (
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{result.understood_label}</p>
                )}
                {result.total_label && <p className="text-sm text-gray-500 dark:text-gray-400">{result.total_label}</p>}
              </>
            )}
          </div>
          {toggle}
        </div>
        {view === 'map' ? (
          scopedMapPanel ? (
            <AroundMap panel={scopedMapPanel} answerPins={answers} onOpen={onOpen} onOpenDrill={onOpenDrill} />
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No map available for these results.</p>
          )
        ) : answers.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">{result.none_fit_label}</p>
        ) : (
          <div className="space-y-2 focus:outline-none flex-1" tabIndex={0} onKeyDown={onAnswerListKey} aria-label="Answers — j and k to move, Enter to open">
            {pageItems.map((a, i) => {
              const globalIndex = (page - 1) * ANSWER_PAGE_SIZE + i;
              return (
                <AnswerCard key={a.entity_id} answer={a} index={globalIndex} active={globalIndex === answerActiveIdx} onOpen={onOpen} onBook={onBook} onOutcome={onOutcome} />
              );
            })}
          </div>
        )}
        {view === 'list' && (
          <div className="mt-auto">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} perPage={ANSWER_PAGE_SIZE} label="places" alwaysShow />
          </div>
        )}
      </div>
    );
  }

  // No query yet — the "Open right now" default suggestions, same AnswerCard as the results.
  return (
    <div className="flex flex-col gap-3" data-testid="li-right-now">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Open right now</h2>
        {toggle}
      </div>
      {view === 'map' ? (
        scopedMapPanel ? (
          <AroundMap panel={scopedMapPanel} answerPins={answers} onOpen={onOpen} onOpenDrill={onOpenDrill} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No map available right now.</p>
        )
      ) : answers.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No suggestions right now.</p>
      ) : (
        <div className="space-y-2 flex-1">
          {pageItems.map((a, i) => {
            const globalIndex = (page - 1) * ANSWER_PAGE_SIZE + i;
            return (
              <AnswerCard key={a.entity_id} answer={a} index={globalIndex} onOpen={onOpen} onBook={onBook} onOutcome={onOutcome} />
            );
          })}
        </div>
      )}
      {view === 'list' && (
        <div className="mt-auto">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} perPage={ANSWER_PAGE_SIZE} label="places" alwaysShow />
        </div>
      )}
    </div>
  );
};

// ─── Item 2 — venue row: category icon + meta chips ─────────────────────────
// The drill "list" frame used to render each place via PlaceRow, which joined
// category · drive · hours into one run-on text line. Item 2 restructures the
// row here (PlaceRow stays untouched for OpenTonight): a category icon + name on
// the name line, OPEN/CLOSED badge on the right, and the meta info rendered as
// separate chips below — the same chip style as AnswerCard/HelpRail. Inline
// stroke glyphs follow HelpRail.js (no icon library is installed). The whole
// row still opens the place via onOpen → ctx.onOpenDrill(p.drill) (fn_li_place_detail).
const Glyph = ({ children, className = '' }) => (
  <svg className={`w-4 h-4 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const MountainIcon = () => <Glyph><path d="m8 3 4 8 5-5 5 15H2L8 3z" /></Glyph>;
const UtensilsIcon = () => <Glyph><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></Glyph>;
const BeerIcon = () => <Glyph><path d="M17 11h1a3 3 0 0 1 0 6h-1" /><path d="M9 12v6" /><path d="M13 12v6" /><path d="M14 7.5c-1.3-1-2.7-1-4 0V4c1.3-1 2.7-1 4 0v3.5Z" /><path d="M3 9h13v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /></Glyph>;
const CoffeeIcon = () => <Glyph><path d="M10 2v2" /><path d="M14 2v2" /><path d="M6 2v2" /><path d="M5 6h10v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4Z" /><path d="M17 8h1a2 2 0 1 1 0 4h-1" /></Glyph>;
const WineIcon = () => <Glyph><path d="M8 22h8" /><path d="M12 15v7" /><path d="M5 3h14l-1 9a6 6 0 0 1-12 0z" /></Glyph>;
const FuelIcon = () => <Glyph><path d="M3 22h8V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" /><path d="M3 9h8" /><path d="M14 9V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-3" /></Glyph>;
const PinIcon = () => <Glyph><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></Glyph>;

// Matched against the row's category_label. The lighter closed/new drill rows
// carry no category_label, so they fall back to the map pin (matches HelpRail).
const VENUE_ICONS = [
  { test: /coffee|cafe|espresso/i, Icon: CoffeeIcon },
  { test: /brew|brewery|beer|pub|taproom/i, Icon: BeerIcon },
  { test: /wine|winery|vineyard/i, Icon: WineIcon },
  { test: /hiking|trail|trek/i, Icon: MountainIcon },
  { test: /restaurant|sandwich|hamburger|food|diner|grill|kitchen/i, Icon: UtensilsIcon },
  { test: /gas station|fuel|petrol/i, Icon: FuelIcon },
];
const iconForVenue = (categoryLabel) => {
  const hay = categoryLabel || '';
  const match = VENUE_ICONS.find((c) => c.test.test(hay));
  const Icon = match ? match.Icon : PinIcon;
  return <Icon />;
};

// Chip style — identical to the AnswerCard / HelpRail meta chips.
const META_CHIP = 'text-sm px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 whitespace-nowrap';

// rating_label arrives as "4.4 from 537 reviews"; Item 2 wants "4.4 · 537 reviews".
// Transform the separator, fall back to the raw label, then to rating + rating_count.
const RATING_LABEL_RE = /^(.+?)\s+from\s+(.+)$/i;
const ratingChipFor = (p) => {
  if (p.rating_label) {
    const m = String(p.rating_label).match(RATING_LABEL_RE);
    return m ? `${m[1]} · ${m[2]}` : String(p.rating_label);
  }
  if (p.rating != null && p.rating_count != null) return `${p.rating} · ${p.rating_count} reviews`;
  if (p.rating != null) return String(p.rating);
  return '';
};
// Drive chip — sourced from drive_minutes (spec), with distance_label ("8 min
// drive") as the backend-formatted fallback when drive_minutes is absent.
const driveChipFor = (p) => (p.drive_minutes != null ? `${p.drive_minutes} min drive` : (p.distance_label || ''));

// The chip-based venue row: category icon + name on the name line, OPEN/CLOSED
// badge on the right, meta chips (open-until, drive, rating) below. Closed/new
// drill rows carry only label/since_label — those render as chips too so nothing
// regresses. The old run-on description line (category · drive · hours) is gone.
const VenueRow = ({ place, onOpen }) => {
  if (!place) return null;
  const metaChips = [place.hours_label, driveChipFor(place), ratingChipFor(place), place.label, place.since_label].filter(Boolean);
  return (
    <button type="button" className={ROW_BUTTON} onClick={() => onOpen(place)} data-testid="li-place-row">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[#32B1FF] shrink-0">{iconForVenue(place.category_label)}</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {place.featured && <span className="text-[#32B1FF]" title="Featured by staff">★ </span>}
              {place.name}
            </span>
          </div>
          {metaChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {metaChips.map((c) => (
                <span key={c} className={META_CHIP}>{c}</span>
              ))}
            </div>
          )}
          {place.note_label && <div className="text-sm text-gray-500 dark:text-gray-400 italic mt-1">{place.note_label}</div>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {place.open_now === true && <span className={PILL.emerald}>Open</span>}
          {place.open_now === false && <span className={PILL.gray}>Closed</span>}
          {place.guest_ready === false && place.guest_ready_label && <span className={PILL.gray}>{place.guest_ready_label}</span>}
        </div>
      </div>
    </button>
  );
};

// The body of every drill-over frame. DrillOver hands over the top frame whether or not
// it has loaded, so this renders the loading and error states itself.
//   kind 'list'    a fn_li_drill result — places, closures, new places or requests
//   kind 'place'   fn_li_place_detail
//   kind 'request' the booking-request form for one place
//   kind 'call'    calling one place from the desk
//   kind 'send'    getting one place to a guest
const LocalDrillBody = ({ frame, clientId, ctx, ...pageProps }) => {
  // Item 3 — frontend "Load more" for the drill list. The list RPC (fn_li_drill)
  // returns 50 rows per call with a separate `total` (the full match count, e.g.
  // 473), so the full set is NOT in memory — only the loaded batch is. The list
  // therefore shows a `visible` slice of the loaded rows; "Load more" reveals the
  // next VENUE_PAGE_SIZE client-side and fetches the next API batch of 50 when the
  // slice outruns what is loaded. `visible` resets whenever the drill changes (a
  // new what/value, or a different frame kind) so a fresh list reopens at
  // VENUE_PAGE_SIZE rows, not at whatever the previous drill had expanded to.
  const drillKey = `${frame?.kind ?? ''}|${frame?.what ?? ''}|${frame?.value ?? ''}`;
  const [visible, setVisible] = useState(VENUE_PAGE_SIZE);
  const [seenKey, setSeenKey] = useState(drillKey);
  if (seenKey !== drillKey) { setSeenKey(drillKey); setVisible(VENUE_PAGE_SIZE); }

  if (!frame) return <PageResultsBody {...pageProps} />;
  if (frame.loading) return <SkeletonText lines={6} lineClass="h-10" />;
  if (frame.error) {
    return <ErrorState message={frame.message || 'Could not load these records.'} variant="strip" onRetry={frame.retry} />;
  }

  if (frame.kind === 'place') {
    return (
      <PlaceDetail detail={frame.detail} onBook={ctx.onBook} onCurate={(action, value) => ctx.onCurate(frame.detail.entity_id, action, value)} />
    );
  }

  if (frame.kind === 'call') {
    return <CallPanel clientId={clientId} place={frame.place} onOutcome={ctx.onOutcome} />;
  }

  if (frame.kind === 'send') {
    return <SendToGuest clientId={clientId} place={frame.place} parkName={ctx.parkName} />;
  }

  if (frame.kind === 'request') {
    return (
      <RequestForm
          clientId={clientId}
          place={frame.place}
          defaultPartySize={ctx.defaultPartySize}
          defaultAt={ctx.defaultAt}
          onCreated={ctx.onRequestCreated}
          onRecord={ctx.onRecordRequest}
          onOpenPlace={ctx.onOpenPlace}
        />
    );
  }

  const rows = Array.isArray(frame.rows) ? frame.rows : [];
  const total = frame.total ?? rows.length;
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Nothing here right now.</p>;
  }

  // Item 1 — sort open venues first (open rows, divider, closed rows). Item 3 —
  // only the first `visible` rows of the open-then-closed order are shown. The
  // list RPC returns 50 rows per call with a separate `total`, so the full set is
  // not in memory: "Load more" reveals the next VENUE_PAGE_SIZE from the loaded
  // batch, and fetches the next API batch of 50 when the visible slice outruns
  // what is loaded. The count reflects what is actually on screen
  // (visibleRows.length), so it never overstates while a batch is in flight.
  const isVenueList = frame.what !== 'requests';
  const openRows = isVenueList ? rows.filter((r) => r.open_now === true) : [];
  const closedRows = isVenueList ? rows.filter((r) => r.open_now !== true) : [];
  const ordered = isVenueList ? [...openRows, ...closedRows] : rows;
  const visibleRows = ordered.slice(0, visible);
  const visibleOpen = isVenueList ? openRows.slice(0, visible) : [];
  const visibleClosed = isVenueList ? closedRows.slice(0, Math.max(0, visible - openRows.length)) : [];
  const showDivider = isVenueList && visibleOpen.length > 0 && visibleClosed.length > 0;

  // Item 3 — reveal the next batch client-side; if that outruns the loaded rows,
  // pull the next API batch of 50 (the full set is server-paged, not in memory).
  const loadMore = () => {
    const nextVisible = Math.min(visible + VENUE_PAGE_SIZE, total);
    setVisible(nextVisible);
    if (nextVisible > rows.length && rows.length < total) ctx.onLoadMore(frame);
  };

  return (
    <div>
      <div className="pb-2 text-sm text-gray-500 dark:text-gray-400">
        Showing {fmtCount(visibleRows.length)} of {fmtCount(total)}
      </div>
      <div className={`-mx-4 md:-mx-6 divide-y divide-gray-100 dark:divide-gray-800 border-y border-gray-100 dark:border-gray-800${isVenueList ? ' [&_button]:cursor-pointer' : ''}`}>
        {frame.what === 'requests'
          ? visibleRows.map((r) => <RequestRow key={r.request_id} row={r} onRecord={ctx.onRecordRequest} onOpenPlace={ctx.onOpenPlace} />)
          : (
            <>
              {visibleOpen.map((r) => <VenueRow key={r.entity_id} place={r} onOpen={(p) => ctx.onOpenDrill(p.drill)} />)}
              {showDivider && <div aria-hidden="true" className="border-t-2 border-gray-200 dark:border-gray-700" />}
              {visibleClosed.map((r) => <VenueRow key={r.entity_id} place={r} onOpen={(p) => ctx.onOpenDrill(p.drill)} />)}
            </>
          )}
      </div>
      {visibleRows.length < total && (
        <div className="pt-4">
          <button type="button" className={BTN} disabled={frame.loadingMore} onClick={loadMore}>
            {frame.loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LocalDrillBody;
